/**
 * Accounts API hooks (Wave 2.12 FE, TASK-073 round 2).
 *
 * Endpoint contract (BE-2, shipped on main as commit `c6adee5`):
 *   GET /admin/accounts?<filters>&cursor=<b64url>&limit=<int>
 *       → { items: AccountListItem[], total: number | null, next_cursor: string | null }
 *
 * Pagination is **keyset-only**. Cursor is an opaque base64url token valid
 * only for the exact filter combo that produced it — switching filters
 * resets it.
 *
 * RBAC: backend enforces admin / super_admin. Selectors / client roles are
 * rejected at the route guard before the query fires.
 *
 * **Wire contract (synced with `packages/shared/src/schemas/account.ts`):**
 *   - `state` — CSV of `AccountSubscriptionState` (`active | trial |
 *     trial_ended | paused | cancelled | archived | none`): where the account
 *     stands by its current subscription (registration-approval arch §2.3,
 *     §7). It replaced the account `status` in v1.3.0.
 *   - `tariff_id` — single UUID (not an array of codes).
 *   - `billing_mode` — `manual | self_service` (no `auto`).
 *   - `paid_until` — nullable `YYYY-MM-DD`, the current subscription's
 *     `period_end`.
 *   - List-row `state` + `billing_mode` are NON-nullable; `tariff_id`,
 *     `tariff_code`, `paid_until`, `region` are nullable.
 */

import { useQuery } from '@tanstack/react-query';
import type { AccountListResponse } from '@music-app/shared';
import { apiClient } from '../lib/api-client';
import { STALE_TIME } from '../lib/query-client';
import type { AccountFilters } from '../hooks/useAccountFilters';

export type { AccountListItem, AccountListResponse } from '@music-app/shared';

export interface AccountsQueryParams {
  filters?: AccountFilters;
  cursor?: string;
  limit?: number;
}

/** Page size used both on the wire (`limit=`) and to size the UI footer.
 * Matches `accountFilterSchema.limit.default`. */
export const ACCOUNTS_PAGE_SIZE = 50;
const ACCOUNTS_ENDPOINT = '/admin/accounts';
/** Mirrors `accountFilterSchema.limit.max` in `@music-app/shared`. */
const MAX_LIMIT = 200;

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Widen a calendar date stored in the URL (`YYYY-MM-DD`) into a strict ISO
 * 8601 datetime so the BE schema accepts it. `paid_until_before` anchors at
 * the end of the day (inclusive upper bound); `paid_until_after` anchors at
 * the start (inclusive lower bound). Anything already in datetime form is
 * passed through unchanged.
 */
function toIsoBoundary(value: string, edge: 'start' | 'end'): string {
  if (!DATE_ONLY_RE.test(value)) return value;
  return edge === 'start' ? `${value}T00:00:00Z` : `${value}T23:59:59Z`;
}

function writeFilters(
  filters: AccountFilters | undefined,
  sp: URLSearchParams,
): void {
  if (!filters) return;
  if (filters.q) sp.set('q', filters.q);
  if (filters.region) sp.set('region', filters.region);
  // Single UUID per `accountFilterSchema.tariff_id`. Multi-tariff filtering
  // would require a BE `tariff_ids` array — out of scope for Wave 2.12.
  if (filters.tariff_id) sp.set('tariff_id', filters.tariff_id);
  // Wire key is `state` (CSV of AccountSubscriptionState). The tab and the
  // «Триал истёк» facet project onto it via `useAccountFilters.tabToStates()`.
  if (filters.state?.length) sp.set('state', filters.state.join(','));
  if (filters.paid_until_before) {
    sp.set('paid_until_before', toIsoBoundary(filters.paid_until_before, 'end'));
  }
  if (filters.paid_until_after) {
    sp.set('paid_until_after', toIsoBoundary(filters.paid_until_after, 'start'));
  }
}

export function buildAccountsSearchParams(
  params: AccountsQueryParams,
): URLSearchParams {
  const sp = new URLSearchParams();
  writeFilters(params.filters, sp);
  const limit = Math.min(
    Math.max(1, params.limit ?? ACCOUNTS_PAGE_SIZE),
    MAX_LIMIT,
  );
  sp.set('limit', String(limit));
  if (params.cursor) sp.set('cursor', params.cursor);
  return sp;
}

export function useAccountsListQuery(
  params: AccountsQueryParams,
  options?: { enabled?: boolean },
) {
  return useQuery<AccountListResponse>({
    queryKey: ['accounts', 'list', params],
    queryFn: () =>
      apiClient<AccountListResponse>(
        `${ACCOUNTS_ENDPOINT}?${buildAccountsSearchParams(params).toString()}`,
      ),
    staleTime: STALE_TIME.list,
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
}
